<template>
  <div class="home">
    <page-content>
      <div class="row">
        <div class="offset-md-1 col-md-10">
          <div class="block block-rounded">
            <ul class="nav nav-tabs nav-tabs-block" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'venues' }"
                  role="tab"
                  @click="activeTab = 'venues'"
                >
                  <i class="fa fa-map-marker mr-1"></i> {{ $tc('venue.name', 2) }}
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'games' }"
                  role="tab"
                  @click="activeTab = 'games'"
                >
                  <i class="fa fa-list mr-1"></i> {{ $tc('game.name', 2) }}
                </button>
              </li>
            </ul>
            <div class="block-content tab-content" style="min-height: 300px">

              <!-- VENUES TAB -->
              <div class="tab-pane" :class="{ 'active show': activeTab === 'venues' }" role="tabpanel">
                <le-loading v-if="isLoadingVenues"></le-loading>
                <div v-else>
                  <table v-if="venues.length" class="table table-bordered table-striped table-vcenter">
                    <thead>
                      <tr>
                        <th>{{ $tc('venue.name', 1) }}</th>
                        <th class="text-center">{{ $t('table.column.livestreamWidget') }}</th>
                        <th class="text-center">{{ $t('table.column.livestreamTeamStatsOverlay') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="venue in venues" :key="venue.uuid">
                        <td class="font-w600">{{ venue.name }}</td>
                        <td>
                          <div class="d-flex align-items-center justify-content-center">
                            <button class="btn btn-sm btn-circle btn-info mr-1" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(venueBroadcastLink(venue))">
                              <i class="fa fa-copy"></i>
                            </button>
                            <le-q-rcode :link="venueBroadcastLink(venue)" :name="venue.name" class="mr-1" />
                            <button v-if="canShare" class="btn btn-sm btn-circle btn-info flex-shrink-0" v-tooltip="$t('tooltip.share')" @click="shareLink(venueBroadcastLink(venue))">
                              <i class="fa fa-share-alt"></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex align-items-center justify-content-center">
                            <button class="btn btn-sm btn-circle btn-info mr-1" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(venueTeamStatsOverlayLink(venue))">
                              <i class="fa fa-copy"></i>
                            </button>
                            <le-q-rcode :link="venueTeamStatsOverlayLink(venue)" :name="venue.name" class="mr-1" />
                            <button v-if="canShare" class="btn btn-sm btn-circle btn-info flex-shrink-0" v-tooltip="$t('tooltip.share')" @click="shareLink(venueTeamStatsOverlayLink(venue))">
                              <i class="fa fa-share-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="text-center py-4">
                    <p>{{ $tc('venue.name', 0) }}</p>
                  </div>
                </div>
              </div>

              <!-- GAMES TAB -->
              <div class="tab-pane" :class="{ 'active show': activeTab === 'games' }" role="tabpanel">
                <le-loading v-if="isLoadingGames"></le-loading>
                <div v-else>
                  <div v-if="gamesVenues.length > 1" class="mb-3">
                    <multiselect
                      class="w-auto"
                      style="max-width: 400px"
                      :multiple="false"
                      :placeholder="$t('venue.all')"
                      :custom-label="(option) => option.name"
                      v-model="gamesVenueFilter"
                      :options="gamesVenues"
                    ></multiselect>
                  </div>
                  <table v-if="filteredGames.length" class="table table-bordered table-striped table-vcenter">
                    <thead>
                      <tr>
                        <th>{{ $t('time') }}</th>
                        <th>{{ $tc('game.name', 1) }}</th>
                        <th>{{ $tc('league.name', 1) }}</th>
                        <th>{{ $tc('venue.name', 1) }}</th>
                        <th>{{ $t('table.column.livestreamWidget') }}</th>
                        <th>{{ $t('table.column.livestreamTeamStatsOverlay') }}</th>
                        <th>{{ $t('table.column.event') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="game in filteredGames" :key="game.uuid" :class="gameStatusColor(game.status)">
                        <td class="no-wrap">{{ $gameTime(game.time, 'dayWithTime') }}</td>
                        <td>
                          <strong>{{ game.teamA?.name ?? '?' }}</strong> : <strong>{{ game.teamB?.name ?? '?' }}</strong>
                        </td>
                        <td>{{ game.schedule.name }}</td>
                        <td>{{ game.venue?.name ?? '-' }}</td>
                        <td>
                          <div v-if="gameBroadcastLink(game)" class="d-flex align-items-center">
                            <button class="btn btn-sm btn-circle btn-info flex-shrink-0 mr-1" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(gameBroadcastLink(game))">
                              <i class="fa fa-copy"></i>
                            </button>
                            <le-q-rcode :link="gameBroadcastLink(game)" :name="game.slug" class="mr-1" />
                            <button v-if="canShare" class="btn btn-sm btn-circle btn-info flex-shrink-0" v-tooltip="$t('tooltip.share')" @click="shareLink(gameBroadcastLink(game))">
                              <i class="fa fa-share-alt"></i>
                            </button>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <div v-if="gameTeamStatsOverlayLink(game)" class="d-flex align-items-center">
                            <button class="btn btn-sm btn-circle btn-info flex-shrink-0 mr-1" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(gameTeamStatsOverlayLink(game))">
                              <i class="fa fa-copy"></i>
                            </button>
                            <le-q-rcode :link="gameTeamStatsOverlayLink(game)" :name="game.slug" class="mr-1" />
                            <button v-if="canShare" class="btn btn-sm btn-circle btn-info flex-shrink-0" v-tooltip="$t('tooltip.share')" @click="shareLink(gameTeamStatsOverlayLink(game))">
                              <i class="fa fa-share-alt"></i>
                            </button>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <div v-if="hasYoutubeConnectedAccount || game.embedVideoSourceType !== 'YOUTUBE'" class="d-flex align-items-center">
                            <template v-if="hasYoutubeConnectedAccount">
                              <div
                                v-if="game.embedVideoSourceType !== 'YOUTUBE'"
                                class="yt-create-badge btn-dual mr-1"
                                :class="{ 'yt-create-badge--disabled': ytLoadingGame === game.uuid }"
                                @click="ytLoadingGame !== game.uuid && createYtEvent(game)"
                              >
                                <span class="mr-1" style="position: relative; display: inline-flex; font-size: 18px; line-height: 1;">
                                  <i class="fa fa-youtube-play" style="color: #dc3545;"></i>
                                  <i class="fa fa-play" style="position: absolute; top: 50%; left: 53%; transform: translate(-50%, -50%); font-size: 0.42em; color: #fff;"></i>
                                </span>
                                <span>{{ $t('createEvent') }}</span>
                                <span class="yt-create-badge__icon ml-2" style="font-size: 14px;">
                                  <i v-if="ytLoadingGame === game.uuid" class="fa fa-cog fa-spin"></i>
                                  <i v-else class="fa fa-plus yt-create-badge__icon--green"></i>
                                </span>
                              </div>
                              <div v-else class="yt-active-badge bg-success mr-1">
                                <span class="yt-active-badge__label">
                                  <span class="mr-1" style="position: relative; display: inline-flex; font-size: 18px; line-height: 1;">
                                    <i class="fa fa-youtube-play" style="color: #dc3545;"></i>
                                    <i class="fa fa-play" style="position: absolute; top: 50%; left: 53%; transform: translate(-50%, -50%); font-size: 0.42em; color: #fff;"></i>
                                  </span>
                                  <span>{{ $t('eventCreated') }}</span>
                                </span>
                                <button
                                  :disabled="ytLoadingGame === game.uuid"
                                  class="yt-active-badge__delete btn-danger"
                                  @click="deleteYtEvent(game)"
                                >
                                  <i v-if="ytLoadingGame === game.uuid" class="fa fa-cog fa-spin"></i>
                                  <i v-else class="fa fa-trash-o"></i>
                                </button>
                              </div>
                            </template>
                            <button v-if="game.embedVideoSourceType !== 'YOUTUBE'" class="btn btn-sm btn-primary" v-tooltip="$t('formly.fields.livestreamEvent')" @click="openEmbedVideoModal(game)">
                              <i class="fa fa-video-camera"></i>
                            </button>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="text-center py-4">
                    <p>{{ $t('game.noGamesToday') }}</p>
                  </div>
                </div>
              </div>

            </div>
            <ntm-modal size="lg" ref="embedVideo">
              <form id="embedVideo" @submit.prevent="onEmbedVideoSubmit()">
                <formly-form :form="embedVideoForm" :model="embedVideoModel" :fields="embedVideoFields"></formly-form>
                <button class="btn btn-success push-15">{{ $t('save') }}</button>
              </form>
            </ntm-modal>
          </div>
        </div>
      </div>
    </page-content>
  </div>
</template>

<script>
import ApiService from '@/services/api.service'
import GameService from '@/services/game.service'
import MediaRoleService from '@/services/mediaRole.service'
import LeLoading from '@/components/le/LeLoading.vue'
import LeQRcode from '@/components/le/LeQRcode.vue'
import Multiselect from 'vue-multiselect'
import { gameStatusColor } from '@/lib/status'
import { shareUrl } from '@/lib/share'
import { useExternalLinks } from '@/use/useExternalLinks'
import { useRouteQuery } from '@/use/useRouteQuery'
import embedVideoFields from '@/formDefinitions/embedVideo'

export default {
  components: { LeLoading, LeQRcode, Multiselect },
  setup () {
    const activeTab = useRouteQuery('tab', 'venues')
    return { activeTab }
  },
  data () {
    return {
      games: [],
      venues: [],
      hasYoutubeConnectedAccount: false,
      isLoadingGames: false,
      isLoadingVenues: false,
      ytLoadingGame: null,
      gamesVenueFilter: null,
      embedVideoForm: {},
      embedVideoModel: {
        code: ''
      },
      embedVideoFields: embedVideoFields,
      embedVideoGame: null
    }
  },
  mounted () {
    this.fetchGames()
    this.fetchVenues()
    document.addEventListener('visibilitychange', this.fetchGames)
  },
  beforeDestroy () {
    document.removeEventListener('visibilitychange', this.fetchGames)
  },
  computed: {
    canShare () {
      return !!navigator.share
    },
    gamesVenues () {
      const uuids = new Set(this.games.map((game) => game.venue?.uuid).filter(Boolean))
      return this.venues.filter((venue) => uuids.has(venue.uuid))
    },
    filteredGames () {
      if (!this.gamesVenueFilter) return this.games
      return this.games.filter((game) => game.venue?.uuid === this.gamesVenueFilter.uuid)
    }
  },
  methods: {
    gameStatusColor,
    shareLink (url) {
      return shareUrl(url)
    },
    fetchGames () {
      this.isLoadingGames = true
      MediaRoleService.games('today').then((response) => {
        this.games = response.data.games
        this.hasYoutubeConnectedAccount = response.data.hasYoutubeConnectedAccount
      }).finally(() => {
        this.isLoadingGames = false
      })
    },
    fetchVenues () {
      this.isLoadingVenues = true
      MediaRoleService.venues().then((response) => {
        this.venues = response.data.venues
      }).finally(() => {
        this.isLoadingVenues = false
      })
    },
    gameWebsiteLink (game) {
      if (!game.seasonSlug || !game.leagueSlug) return null
      const { getWebsiteDomain } = useExternalLinks()
      return `${getWebsiteDomain()}/seasons/${game.seasonSlug}/leagues/${game.leagueSlug}/rounds/${game.round}/games/${game.slug}`
    },
    gameBroadcastLink (game) {
      const link = this.gameWebsiteLink(game)
      return link && `${link}/broadcast`
    },
    gameTeamStatsOverlayLink (game) {
      const link = this.gameWebsiteLink(game)
      return link && `${link}/team-stats-overlay`
    },
    venueBroadcastLink (venue) {
      const { getWebsiteDomain } = useExternalLinks()
      return `${getWebsiteDomain()}/venues/${venue.uuid}/broadcast-game`
    },
    venueTeamStatsOverlayLink (venue) {
      const { getWebsiteDomain } = useExternalLinks()
      return `${getWebsiteDomain()}/venues/${venue.uuid}/team-stats-overlay`
    },
    copyToClipboard (text) {
      navigator.clipboard.writeText(text)
    },
    createYtEvent (game) {
      this.ytLoadingGame = game.uuid
      ApiService.setGameCode(game.code)
      GameService.createYtEvent().then(() => {
        game.embedVideoSourceType = 'YOUTUBE'
      }).catch(() => {}).finally(() => {
        this.ytLoadingGame = null
      })
    },
    deleteYtEvent (game) {
      this.ytLoadingGame = game.uuid
      ApiService.setGameCode(game.code)
      GameService.deleteYtEvent().then(() => {
        game.embedVideoSourceType = null
      }).catch(() => {}).finally(() => {
        this.ytLoadingGame = null
      })
    },
    openEmbedVideoModal (game) {
      this.embedVideoGame = game
      this.embedVideoModel.code = game.embedVideoCode || ''
      ApiService.setGameCode(game.code)
      this.$refs.embedVideo.open()
    },
    onEmbedVideoSubmit () {
      GameService.embedVideo(this.embedVideoModel).then(() => {
        this.embedVideoGame.embedVideoCode = this.embedVideoModel.code
        this.embedVideoGame.embedVideoSourceType = null
        this.$refs.embedVideo.close()
      })
    }
  }
}
</script>
